"use client"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar, ThumbsUp, CheckCircle2 } from "lucide-react"
import type { Review } from "@/types/review"
import { getAvatarColor, getInitials } from "@/utils/avatar-utils"
import { useTranslations } from "@/hooks/use-translations"
import { isMarkedAsHelpful } from "@/utils/helpful-uitils"
import { useState } from "react"

interface ReviewDetailProps {
  review: Review
  onMarkHelpful: (reviewId: string) => void
}

export function ReviewDetail({ review, onMarkHelpful }: ReviewDetailProps) {
  const { t } = useTranslations()
  const [helpfulCount, setHelpfulCount] = useState<number>(review.helpful || 0)
  const handleHelpfulClick = () => {
    setHelpfulCount(helpfulCount + 1)
    onMarkHelpful(review.id)
  }
  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <Avatar className={`w-12 h-12 ${getAvatarColor(review.name)}`}>
          <AvatarFallback className="text-white font-semibold bg-transparent">
            {getInitials(review.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{review.name}</h3>
            {review.verified ? (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-xs">{t("verified")}</span>
              </div>
            ) : (
              <span className="text-xs text-gray-500">{t("notVerified")}</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {review.role}{review.company && (" " + t("atCompany", { company: review.company }))}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Badge variant="outline" className="text-sm">
              {t("rating")}: {review.rating}/5
            </Badge>
            <Badge variant="secondary">{review.companySize}</Badge>
            <Badge variant="secondary">{review.sector}</Badge>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold mb-2">{review.title}</h4>
        <p className="text-sm leading-relaxed">
          {review.fullReview
            .split(/\r?\n/)
            .map((line, idx, arr) =>
              line.trim() === "" && idx === arr.length - 1
                ? null
                : (
                  <span key={idx}>
                    {line}
                    {idx < arr.length - 1 && <br />}
                  </span>
                )
            )}
        </p>
      </div>

      {(review.pros.length > 0 || review.cons.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {review.pros.length > 0 && (
            <div>
              <h5 className="font-medium text-green-700 mb-2">{t("advantages")}</h5>
              <ul className="space-y-1">
                {review.pros.map((pro, index) => (
                  <li key={index} className="text-sm flex items-start gap-2">
                    <span className="text-green-500">+</span>
                    <span>{pro}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {review.cons.length > 0 && (
            <div>
              <h5 className="font-medium text-red-700 mb-2">{t("disadvantages")}</h5>
              <ul className="space-y-1">
                {review.cons.map((con, index) => (
                  <li key={index} className="text-sm flex items-start gap-2">
                    <span className="text-red-500">-</span>
                    <span>{con}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>{review.userType || t("usageStatusUnknown")}</span>
        </div>
        {
          isMarkedAsHelpful(review.id) ?
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 text-sm bg-blue-50 text-blue-600  border-blue-200 hover:bg-blue-50 hover:text-blue-600  hover:border-blue-200"
            >
              <ThumbsUp className="w-4 h-4" />
              <span>
                {helpfulCount} {t("helpful")}
              </span>
            </Button>
            :
            <Button
              variant="outline"
              size="sm"
              onClick={handleHelpfulClick}
              className="flex items-center gap-2 text-sm hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
            >
              <ThumbsUp className="w-4 h-4" />
              <span>
                {helpfulCount} {t("helpful")}
              </span>
            </Button>
        }
      </div>
    </div>

  )
}
